import React, { useContext, useEffect, useState } from "react";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../ui/form";
import {
  BuilderProfile,
  CompanyType,
  DeveloperProfile,
  MarketParticipation,
  UnionAffiliationItem,
} from "../../models/company";
import { UserContext } from "../../contexts/UserContext";
import {
  withRequiredAuthInfo,
  WithLoggedInAuthInfoProps,
} from "@propelauth/react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { AutosizeTextarea } from "../ui/autoresizeTextArea";
import { useToast } from "../ui/use-toast";
import { Autocomplete } from "@react-google-maps/api";
import RegionMapComponent from "../googleMaps/RegionMapComponent";
import CompanyInputTable from "./CompanyFormTable";
import {
  marketsColumns,
  UnionAffiliationColumns,
} from "./CompanyBaseFormColumn";
import { useGoogleMapsAutocomplete } from "../../hooks/useGoogleMapsAutocomplete";
import { handleAddressCompanyChanged } from "../googleMaps/handleChangheAddress";

import {USDonChange, USDtoValue} from "../fields/USDField";
import {patchWithToken} from "../../api/apiBase";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";

interface CompanyBaseViewFormProps {
  methods?: ReturnType<typeof useForm<BuilderProfile | DeveloperProfile>>;
  companyProfile: BuilderProfile | DeveloperProfile | null;
}

type CombinedProps = WithLoggedInAuthInfoProps & CompanyBaseViewFormProps;

const CompanyBaseViewForm: React.FC<CombinedProps> = ({
  companyProfile,
  accessToken,
}) => {
  const context = useContext(UserContext);
  const [enabled, setEnabled] = useState<boolean>(false);
  const { toast } = useToast();

  const havePermissionEditing =
    context!.company_permissions?.edit_company_settings &&  // company level permission
    context?.company_id === companyProfile?.id;             // user is of the same company
  const editing = havePermissionEditing && enabled;

  const { autocompleteAddressRef, isLoaded, loadError } =
    useGoogleMapsAutocomplete();

  const [affiliations, setAffiliations] = useState<UnionAffiliationItem[]>([]);
  const [markets, setMarkets] = useState<MarketParticipation[]>([
    { sector: "", subsector: "", project_size_min: 0, project_size_max: 0 },
  ]);
  const [regions, setRegions] = useState<string[]>([]);

  const methods = useForm<Partial<BuilderProfile> | Partial<DeveloperProfile>>({
    defaultValues: {
      name: context?.company_name || "",
      description: companyProfile?.description || "",
      logo: companyProfile?.logo || "",
      address: companyProfile?.address
        ? {
            address: companyProfile.address.address,
            coordinates: {lat: companyProfile.address.coordinates?.lat || 0, lon: companyProfile.address.coordinates?.lon || 0}
          }
        : {
            address: "",
          },
      website: companyProfile?.website || "",
    },
  });

  function isBuilderProfile(
    profile: BuilderProfile | DeveloperProfile | null
  ): profile is BuilderProfile {
    return profile?.company_type === CompanyType.builder;
  }

  useEffect(() => {
    if (
      context?.company_type === "builder" &&
      isBuilderProfile(companyProfile)
    ) {
      methods.setValue("markets", companyProfile?.markets || []);
      methods.setValue("regions", companyProfile?.regions || []);
      methods.setValue("facts", companyProfile?.facts || []);
      // methods.setValue(
      //   "construction_types",
      //   companyProfile?.construction_types || [""]
      // );
      methods.setValue(
        "union_affiliation",
        companyProfile?.union_affiliation || []
      );
      methods.setValue("revenue", companyProfile?.revenue || 0);
      methods.setValue("founded", companyProfile?.founded || 0);
      methods.setValue("employee_count", companyProfile?.employee_count || 0);
      methods.setValue(
        "bonding_capacity",
        companyProfile?.bonding_capacity || 0
      );

      setAffiliations(companyProfile?.union_affiliation);
      setMarkets(companyProfile?.markets);
      setRegions(companyProfile?.regions);
    }
  }, [companyProfile]);

  useEffect(() => {
    if (
      context?.company_type === "builder" &&
      isBuilderProfile(companyProfile)
    ) {
      methods.setValue("union_affiliation", affiliations);
      methods.setValue("markets", markets);
      methods.setValue("regions", regions);
    }
  }, [context?.company_type, companyProfile, markets, affiliations, regions]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  const onSubmit: SubmitHandler<
    Partial<BuilderProfile> | Partial<DeveloperProfile>
  > = (data) => {
    console.log("Form submitted:", data);

    patchWithToken(`/company/${context!.company_id}/profile`, JSON.stringify(data), accessToken)
      .then((response) => {
        console.log(response)
        if (response.message ==='Generic Error ') {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to save",
          });
        }
        else {
          toast({
            variant: "default",
            title: "Success",
            description: "Company Profile Updated",
          });
        }
      })
      .catch((err) => {
        console.log(err);
        toast({
          variant: "destructive",
          title: "Error",
          description: String(err) || "Failed to save",
        });
      });
  };

  const validateMarkets = (data: MarketParticipation[]) => {
    const validatedData = data.map((item) => {
      const projectSizeMinStr = String(item.project_size_min).replace(
        /[^0-9.]/g,
        ""
      );
      const projectSizeMaxStr = String(item.project_size_max).replace(
        /[^0-9.]/g,
        ""
      );

      const projectSizeMin = parseFloat(projectSizeMinStr);
      const projectSizeMax = parseFloat(projectSizeMaxStr);

      if (isNaN(projectSizeMin)) {
        // console.error(`Invalid project_size_min: ${item.project_size_min}`);
      }

      if (isNaN(projectSizeMax)) {
        // console.error(`Invalid project_size_max: ${item.project_size_max}`);
      }

      return {
        ...item,
        project_size_min: !isNaN(projectSizeMin) ? projectSizeMin : 0,
        project_size_max: !isNaN(projectSizeMax) ? projectSizeMax : 0,
      };
    });

    setMarkets(validatedData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          if (context) {
            canvas.width = img.width;
            canvas.height = img.height;
            context.drawImage(img, 0, 0);

            const webpDataUrl = canvas.toDataURL("image/webp", 0.8);
            methods.setValue("logo", webpDataUrl);
            // const webpSize = calculateDataUrlSize(webpDataUrl);
            // console.log(`Original file size: ${files[0].size} bytes`);
            // console.log(`Converted file size: ${webpSize} bytes`);
          } else {
            console.error("error");
          }
        };
      };
      reader.readAsDataURL(files[0]);
    }
  };

  // const calculateDataUrlSize = (dataUrl: string): number => {
  //   const base64String = dataUrl.split(',')[1];
  //   const byteString = atob(base64String);
  //   return byteString.length;
  // };

  return (
    <div className="w-[100%] p-4 bg-white rounded shadow-md max-w-6xl mx-auto">
      <div>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            {havePermissionEditing && (
              <>
                
                <div className="mb-4 mr-5 flex justify-end"></div>
              </>
            )}

            <div className="w-full">
              <div className="flex justify-between mb-1">
                <FormField
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        {/*{editing ? (*/}
                        {/*  <Input*/}
                        {/*    className="w-full"*/}
                        {/*    placeholder="Enter Company Name"*/}
                        {/*    {...field}*/}
                        {/*    value={field.value || ""}*/}
                        {/*    onKeyDown={handleKeyDown}*/}
                        {/*  />*/}
                        {/*) : (*/}
                        <div className="mt-2">
                          <span>{field.value || "N/A"}</span>
                        </div>
                        {/*)}*/}
                      </FormControl>
                      <FormMessage></FormMessage>
                    </FormItem>
                  )}
                />
                <div className="col-span-2 flex justify-end items-center gap-4">
                  {/*<p className="self-start">Company Profile</p>*/}
                  <Button variant="outline" type="submit">
                    Save
                  </Button>
                  <div>
                  <label htmlFor="editing" className="mr-2 font-medium">
                    Editing:
                  </label>
                  <input
                    id="editing"
                    type="checkbox"
                    checked={enabled}
                    onChange={() => setEnabled(!enabled)}
                  />
                  </div>
                </div>
              </div>
              <hr />
            </div>

            <div>
              <div className="grid grid-cols-2 mt-7 gap-x-5">
                <div>
                  <FormField
                    name="logo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel></FormLabel>
                        <FormControl>
                          {editing ? (
                            <div
                            // className="flex flex-col items-center justify-center"
                            >
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                id="file-upload"
                                onChange={handleFileChange}
                              />
                              <label
                                htmlFor="file-upload"
                                className="custom-upload-button"
                              >
                                <span className="h-9 px-4 py-2 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground nline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50">
                                  change logo
                                </span>
                              </label>
                            </div>
                          ) : (
                            <div className="!mt-0 ml-5">
                              {field.value ? (
                                <img
                                  src={field.value as string}
                                  alt="Logo"
                                  className="w-32 h-32 object-cover rounded-full"
                                />
                              ) : (
                                <span></span>
                              )}
                            </div>
                          )}
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-6">
                  <FormField
                    name="address.address"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-5">
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          {enabled && isLoaded ? (
                            <Autocomplete
                              className="w-full"
                              onLoad={(autocomplete) =>
                                (autocompleteAddressRef.current = autocomplete)
                              }
                              onPlaceChanged={() =>
                                handleAddressCompanyChanged(
                                  field,
                                  autocompleteAddressRef,
                                  methods
                                )
                              }
                            >
                              <Input
                                className="!mt-0"
                                placeholder="Enter Address"
                                {...field}
                                value={field.value || ""}
                                onKeyDown={handleKeyDown}
                                // onBlur={(e) => {
                                //   const newValue = e.target.value;
                                //   setLocation((prev) => ({
                                //     ...prev,
                                //     address: newValue,
                                //   }));
                                // }}
                              />
                            </Autocomplete>
                          ) : (
                            <div className="!mt-0 ml-5">
                              <span>{field.value || "N/A"}</span>
                            </div>
                          )}
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="website"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-5">
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          {editing ? (
                            <Input
                              className="w-full !mt-0"
                              placeholder="Enter Website URL"
                              {...field}
                              value={field.value || ""}
                              onKeyDown={handleKeyDown}
                              onChange={(e) => {
                                  if(e.target.value === ""){
                                    field.onChange(null)
                                  } else {
                                    field.onChange(e.target.value)
                                  }
                            }}
                            />
                          ) : (
                            <div className="!mt-0 ml-5">
                              <span>{field.value || "N/A"}</span>
                            </div>
                          )}
                        </FormControl>
                        <FormMessage></FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="mb-5 mt-5">
                <hr />
              </div>
            </div>

            {/* for builder */}
            {context?.company_type === "builder" && (
              <>
                <div className="grid grid-cols-2 gap-x-5">
                  <div className="grid gap-6">
                    <FormField
                      name="employee_count"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-5">
                          <FormLabel>Employees:</FormLabel>
                          <FormControl>
                            {editing ? (
                              <Input
                                className="w-full !m-0"
                                placeholder="Enter Employee Count"
                                {...field}
                                value={field.value || ""}
                                onKeyDown={handleKeyDown}
                              />
                            ) : (
                              <div className="!m-0">
                                <span>
                                  {field.value !== null ? field.value : "N/A"}
                                </span>
                              </div>
                            )}
                          </FormControl>
                          <FormMessage></FormMessage>
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="founded"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-[34px]">
                          <FormLabel>Founded:</FormLabel>
                          <FormControl>
                            {editing ? (
                              <Input
                                className="w-full !m-0"
                                placeholder="Enter Founded Year"
                                {...field}
                                value={field.value || ""}
                                onKeyDown={handleKeyDown}
                              />
                            ) : (
                              <div className="!m-0">
                                <span>
                                  {field.value !== null ? field.value : "N/A"}
                                </span>
                              </div>
                            )}
                          </FormControl>
                          <FormMessage></FormMessage>
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="revenue"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-[34px]">
                          <FormLabel>Revenue</FormLabel>
                          <FormControl>
                            {editing ? (
                              <Input
                                className="w-full !m-0"
                                placeholder="Enter Revenue"
                                {...field}
                                value={USDtoValue(field.value)}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => {
                                  field.onChange(USDonChange(e.target.value));
                                }}
                              />
                            ) : (
                              <div className="!mt-0 ml-[50px]">
                                <span>{USDtoValue(field.value)}</span>
                              </div>
                            )}
                          </FormControl>
                          <FormMessage></FormMessage>
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="bonding_capacity"
                      render={({ field }) => (
                        <FormItem className="flex items-center">
                          <FormLabel>Bonding Capacity</FormLabel>
                          <FormControl>
                            {editing ? (
                              <Input
                                className="w-full !m-0"
                                placeholder="Enter Bonding Capacity"
                                {...field}
                                value={USDtoValue(field.value)}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => {
                                  field.onChange(USDonChange(e.target.value));
                                }}
                              />
                            ) : (
                              <div className="!mt-0 ml-6">
                                <span>{USDtoValue(field.value)}</span>
                              </div>
                            )}
                          </FormControl>
                          <FormMessage></FormMessage>
                        </FormItem>
                      )}
                    />

                    <div className="flex flex-col gap-4">
                      <FormLabel>Markets:</FormLabel>
                      <CompanyInputTable
                        columns={marketsColumns}
                        data={markets}
                        editing={editing}
                        onDataChange={validateMarkets}
                      />
                    </div>

                    <div className="flex flex-col gap-4">
                      <FormLabel>Union Affiliation:</FormLabel>
                      <CompanyInputTable
                        columns={UnionAffiliationColumns}
                        data={affiliations}
                        editing={editing}
                        onDataChange={setAffiliations}
                      />
                    </div>
                  </div>

                  <div className="">
                    <div className="min-w-full">
                      <p className="mb-2 text-sm font-medium">Regions:</p>
                      {!isLoaded ? (
                        <div><BaseLoadingSpinner/></div>
                      ) : (
                        <RegionMapComponent
                          regions={regions}
                          setRegions={setRegions}
                          isEditing={editing}
                        />
                      )}
                      {loadError && <div>Error to load Map</div>}
                    </div>
                  </div>
                </div>
              </>
            )}

            <FormField
              name="description"
              render={({ field }) => (
                <FormItem className="col-span-2 mt-6">
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    {editing ? (
                      <AutosizeTextarea
                        className="w-full min-h-20"
                        placeholder="Enter or upload Description"
                        {...field}
                        value={field.value || ""}
                      />
                    ) : (
                      <div className="mt-2">
                        <span>{field.value || "N/A"}</span>
                      </div>
                    )}
                  </FormControl>
                  <FormMessage></FormMessage>
                </FormItem>
              )}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default withRequiredAuthInfo(CompanyBaseViewForm);
